// src/firebase/services.js
import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc, setDoc,
  query, where, arrayUnion, arrayRemove, serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './config';
import { v4 as uuidv4 } from 'uuid';

// ─── AGREEMENT ID ─────────────────────────────────────────────
export const generateAgreementID = () =>
  uuidv4().replace(/-/g, '').slice(0, 12);

// ─── AGREEMENTS ───────────────────────────────────────────────

export const createAgreement = async (data) => {
  const agreementID = generateAgreementID();
  const docRef = await addDoc(collection(db, 'agreements'), {
    agreementID,
    type: data.type || 'agreement',
    title: data.title,
    details: data.details,
    createdByName: data.createdByName,
    createdByUID: data.createdByUID,
    partnerName: data.partnerName || '',
    participants: data.participants || [],
    participantCount: data.participantCount || 2,
    status: 'Pending',
    archived: false,
    createdTime: serverTimestamp(),
    confirmedTime: null,
    deadline: data.deadline || null,
    appointmentDate: data.appointmentDate || null,
    amount: data.amount || null,
    currency: data.currency || 'USD',
    evidence: data.evidence || [],
    isPremium: data.isPremium || false,
    creatorZelleId: data.creatorZelleId || '',
    creatorVenmoUsername: data.creatorVenmoUsername || '',
    creatorPaypalUsername: data.creatorPaypalUsername || '',
    creatorMercadoPagoUsername: data.creatorMercadoPagoUsername || '',
    creatorTier: data.creatorTier || 'free',
    verified: data.verified || false,
    participantEmail: data.participantEmail || '',
  });
  await updateDoc(docRef, { firestoreID: docRef.id });
  return { id: docRef.id, agreementID };
};

export const createVerificationToken = async ({ agreementId, email, deadline }) => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const token = Array.from(array, b => b.toString(16).padStart(2, '0')).join('');

  const createdAt = new Date();
  const expiresAt = deadline
    ? new Date(deadline)
    : new Date(createdAt.getTime() + 30 * 24 * 60 * 60 * 1000);

  await setDoc(doc(db, 'verificationTokens', token), {
    token,
    agreementId,
    email,
    createdAt: serverTimestamp(),
    expiresAt: expiresAt.toISOString(),
    status: 'pending',
  });

  return token;
};

export const getAgreementByAgreementID = async (agreementID) => {
  const q = query(collection(db, 'agreements'), where('agreementID', '==', agreementID));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() };
};

export const getAgreementByFirestoreID = async (firestoreID) => {
  const snap = await getDoc(doc(db, 'agreements', firestoreID));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

export const getUserAgreements = async (uid) => {
  const q = query(
    collection(db, 'agreements'),
    where('createdByUID', '==', uid),
    where('archived', '==', false)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const confirmAgreement = async (firestoreID, timezone, confirmerUid) => {
  const fields = {
    status: 'Confirmed',
    confirmedTime: serverTimestamp(),
    confirmedTimezone: timezone || null,
  };
  if (confirmerUid) fields.confirmedByUid = confirmerUid;
  await updateDoc(doc(db, 'agreements', firestoreID), fields);
};

export const declineAgreement = async (firestoreID) => {
  await updateDoc(doc(db, 'agreements', firestoreID), {
    status: 'Declined',
  });
};

export const archiveAgreement = async (firestoreID) => {
  await updateDoc(doc(db, 'agreements', firestoreID), { archived: true });
};

export const restoreAgreement = async (firestoreID) => {
  await updateDoc(doc(db, 'agreements', firestoreID), { archived: false });
};

export const cancelAgreement = async (firestoreID) => {
  await updateDoc(doc(db, 'agreements', firestoreID), {
    status: 'Cancelled',
    cancelledTime: serverTimestamp(),
    archived: true,
  });
};

// Group agreement — confirm one participant
export const confirmGroupParticipant = async (firestoreID, participantName, allParticipants, confirmerUid) => {
  const now = new Date().toISOString();
  const updated = allParticipants.map(p =>
    p.name === participantName ? { ...p, confirmedAt: now, status: 'Confirmed' } : p
  );
  const allConfirmed = updated.every(p => p.status === 'Confirmed');
  const fields = {
    participants: updated,
    ...(allConfirmed ? { status: 'Confirmed', confirmedTime: serverTimestamp() } : {}),
  };
  if (confirmerUid) fields.confirmedByUid = confirmerUid;
  await updateDoc(doc(db, 'agreements', firestoreID), fields);
};

// Mark payment as paid
export const markAsPaid = async (firestoreID, method) => {
  await updateDoc(doc(db, 'agreements', firestoreID), {
    paidAt: serverTimestamp(),
    paymentStatus: 'paid',
    ...(method ? { paidMethod: method } : {}),
  });
};

// ─── EVIDENCE ─────────────────────────────────────────────────

export const uploadEvidence = async (file, agreementID) => {
  const fileName = `${Date.now()}_${file.name}`;
  const storageRef = ref(storage, `evidence/${agreementID}/${fileName}`);
  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);
  return {
    url,
    type: file.type,
    fileName: file.name,
    uploadedAt: new Date().toISOString(),
  };
};

export const addEvidenceToAgreement = async (firestoreID, evidenceItem) => {
  await updateDoc(doc(db, 'agreements', firestoreID), {
    evidence: arrayUnion(evidenceItem),
  });
};

// ─── USERS ────────────────────────────────────────────────────

export const getUserDoc = async (uid) => {
  const snap = await getDoc(doc(db, 'users', uid));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

export const createUserDoc = async (uid, data) => {
  const userRef = doc(db, 'users', uid);
  const snap = await getDoc(userRef);
  if (!snap.exists()) {
    await setDoc(userRef, {
      uid,
      email: data.email || '',
      displayName: data.displayName || '',
      savedAgreements: [],
      tier: 'free',
      createdAt: serverTimestamp(),
      businessName: null,
      businessLogo: null,
    });
  }
};

export const saveAgreementToUser = async (uid, agreementID) => {
  await setDoc(doc(db, 'users', uid), {
    savedAgreements: arrayUnion(agreementID),
  }, { merge: true });
};

export const removeSavedAgreement = async (uid, agreementID) => {
  await updateDoc(doc(db, 'users', uid), {
    savedAgreements: arrayRemove(agreementID),
  });
};

export const getSavedAgreements = async (savedAgreementIDs) => {
  if (!savedAgreementIDs || savedAgreementIDs.length === 0) return [];
  const results = await Promise.all(
    savedAgreementIDs.map(id => getAgreementByAgreementID(id))
  );
  return results.filter(Boolean);
};

export const updateUserTier = async (uid, tier) => {
  await updateDoc(doc(db, 'users', uid), { tier });
};

export const claimVerifiedAgreements = async () => {
  const { getFunctions, httpsCallable } = await import('firebase/functions');
  const fns = getFunctions();
  return httpsCallable(fns, 'claimVerifiedAgreements')();
};
